// components/SearchSelect.js
import React, { useState, useEffect, useCallback, useRef } from 'react'
import { TextField, CircularProgress, Autocomplete, ListItem } from '@mui/material'
import dayjs from 'dayjs'
interface OptionType {
  label: string
  value: number | string | boolean
}

interface MainProps {
  customSelect: (data: any) => void
  fetchCustomOptions: (query: string) => void
  defaultValue: any
  staticOption?: OptionType[]
  placeholder?: string
  value?: any
  classes?: string
  optionSuffixKey?: string
  formatDate?: boolean
  fullWidth?: boolean
}

const SearchSelect = ({
  customSelect,
  defaultValue,
  staticOption,
  fetchCustomOptions,
  placeholder,
  value,
  classes = '',
  optionSuffixKey,
  formatDate = false,
  fullWidth = false
}: MainProps) => {
  const [inputValue, setInputValue] = useState('')
  const [options, setOptions] = useState<any>(staticOption ?? [])
  const [loading, setLoading] = useState(false)
  const isFirstRender = useRef(true)

  // Function to fetch options from API
  const fetchOptions = useCallback(
    async (query: string) => {
      setLoading(true)
      try {
        const resp = await fetchCustomOptions(query)
        setOptions(resp)
      } catch (error) {
        console.error('Failed to fetch options:', error)
        setOptions([])
      }
      setLoading(false)
    },
    [fetchCustomOptions]
  )

  const handleSelect = (e: any, value: any) => {
    const selected = options?.find((ele: any) => ele?.headline == value?.headline)
    if (customSelect) {
      customSelect(selected)
    }
  }

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }
    // Debounce API call — fetch on every change including when input is cleared
    const timeoutId = setTimeout(() => {
      fetchOptions(inputValue)
    }, 500)

    return () => {
      clearTimeout(timeoutId)
    }
  }, [inputValue, fetchOptions])

  return (
    <Autocomplete
      fullWidth={fullWidth}
      options={options}
      filterOptions={x => x}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue)
      }}
      renderOption={(props, option) => (
        <ListItem
          {...props}
          key={option.id}
          className="flex justify-between w-full cursor-pointer hover:bg-slate-100"
        >
          <p className="max-w-[80%] truncate">{option.label}</p>
          {optionSuffixKey && (
            <p>
              {formatDate
                ? option[optionSuffixKey]
                  ? dayjs(option[optionSuffixKey]).format('DD/MM/YY')
                  : ''
                : option[optionSuffixKey]}
            </p>
          )}
        </ListItem>
      )}
      onChange={handleSelect}
      defaultValue={defaultValue}
      renderInput={params => (
        <TextField
          {...params}
          fullWidth={fullWidth}
          placeholder={placeholder ?? 'Select'}
          variant="outlined"
          InputProps={{
            ...params.InputProps,
            className: `${fullWidth ? 'w-full' : 'w-[31.875rem]'} ${classes} rounded-[0.625rem] border-icon-darkgray`,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            )
          }}
        />
      )}
      value={value}
    />
  )
}

export default SearchSelect
